import { AnalyticsBrowser } from "@segment/analytics-next"
import type { Plugin, Analytics } from "@segment/analytics-next"

import {
  isConsolidatedProduct,
  localStorageAvailable,
  sessionStorageAvailable,
} from "./shop"

let analytics: Analytics | null = null

interface VisitedPage {
  sku: string
  first_visit_date: string
  last_visit_date: string
  number_of_visits: number
  url: string
}
interface PageEvent {
  type: "page"
  path: string
  url: string
}
interface TrackEvent {
  type: "track"
  event: string
  data: Record<string, any>
}

interface MissedEvent {
  event: PageEvent | TrackEvent
}

let missed_events: MissedEvent[] = []

const isSegmentEnabled = process.env.GATSBY_SEGMENT_ENABLED === "true"

if (
  process.env.GATSBY_SEGMENT_WRITE_KEY &&
  isSegmentEnabled &&
  typeof window !== "undefined"
) {
  AnalyticsBrowser.load({
    writeKey: process.env.GATSBY_SEGMENT_WRITE_KEY,
  }).then(([response]) => {
    analytics = response
    window.analytics = response
    analytics.register(visitedProductPages)
    analytics.register(socialCookies)
    analytics.register(traits)
    analytics.register(campaignData)
    trackMissedEvents()
  })
}

export const trackMissedEvents = () => {
  if (missed_events.length === 0) return

  const queued_events = missed_events
  missed_events = []

  for (const { event } of queued_events) {
    if (event.type === "page") {
      trackSegmentPageEvent(event.path, event.url)
    } else if (event.type === "track") {
      trackSegmentEvent(event.event, event.data)
    }
  }
}

export const fetchVisitedPages = (): VisitedPage[] => {
  if (localStorageAvailable()) {
    const visitedPages = localStorage.getItem("visitedPages")
    return visitedPages ? JSON.parse(visitedPages) : []
  }

  return []
}

export const setVisitedPages = (pages: VisitedPage[]): void => {
  if (localStorageAvailable()) {
    localStorage.setItem("visitedPages", JSON.stringify(pages))
  }
}

export const campaignData: Plugin = {
  name: "Add utm campaign data to all Events",
  type: "before",
  version: "1.0.0",

  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: (ctx) => {
    ctx.event.context = {
      ...ctx.event?.context,
      ...getStoredCampaign(),
    }
    return ctx
  },
  page: (ctx) => {
    // Store campaign data from first page view in session storage if not already stored
    const campaign = ctx.event.context?.campaign
    if (ctx.event?.anonymousId && campaign) {
      storeCampaignInSessionStorage(campaign)
    }

    ctx.event.context = {
      ...ctx.event?.context,
      ...getStoredCampaign(),
    }
    return ctx
  },
}

export const visitedProductPages: Plugin = {
  name: "store productVisitedPages to cookies",
  type: "before",
  version: "1.0.0",

  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: (ctx) => {
    if (ctx.event?.event === "Product Viewed") {
      const visitedPages = fetchVisitedPages()

      const alreadyVisitedPage = visitedPages.find(
        (page) => page.url === ctx.event?.context?.page?.path
      )

      if (alreadyVisitedPage) {
        alreadyVisitedPage.last_visit_date = new Date().toISOString()
        alreadyVisitedPage.number_of_visits += 1
        setVisitedPages(visitedPages)
      } else {
        const newPage = {
          sku: ctx.event?.properties?.sku,
          first_visit_date: new Date().toISOString(),
          last_visit_date: new Date().toISOString(),
          number_of_visits: 1,
          url: ctx.event?.context?.page?.path,
        } as VisitedPage
        setVisitedPages([...visitedPages, newPage])
      }
    }
    return ctx
  },
}

export const socialCookies: Plugin = {
  name: "Add social cookie values to all Events",
  type: "before",
  version: "1.0.0",

  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: (ctx) => {
    const fbp = getCookie("_fbp")
    if (fbp) {
      ctx.event.context!.fbp = fbp
    }
    const fbc = getCookie("_fbc")
    if (fbc) {
      ctx.event.context!.fbc = fbc
    }
    const gclid = getCookie("_gcl_au")
    if (gclid) {
      ctx.event.context!.gclid = gclid
    }
    return ctx
  },

  page: (ctx) => {
    const fbp = getCookie("_fbp")
    if (fbp) {
      ctx.event.context!.fbp = fbp
    }
    const fbc = getCookie("_fbc")
    if (fbc) {
      ctx.event.context!.fbc = fbc
    }
    const gclid = getCookie("_gcl_au")
    if (gclid) {
      ctx.event.context!.gclid = gclid
    }
    return ctx
  },
}

const fetchUserIpAddress = async (): Promise<string | null> => {
  try {
    const wauCookie = getCookie("wau")
    if (!wauCookie) {
      return null
    }

    const response = await fetch(
      `${process.env.GATSBY_SHOP_API_URL}/signup/hello.json`
    )
    if (!response.ok) {
      console.warn(
        `Failed to fetch IP address: ${response.status} ${response.statusText}`
      )
      return null
    }

    const data = await response.json()
    return data.ip_address || null
  } catch (error) {
    console.warn("Error fetching IP address:", error)
    return null
  }
}

export const traits: Plugin = {
  name: "Add Traits to all Events",
  type: "before",
  version: "1.0.0",

  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: async (ctx) => {
    if (analytics) {
      const user = analytics.user()
      if (user) {
        const traits = analytics.user().traits()
        ctx.event.context!.traits = traits

        const ipAddress = await fetchUserIpAddress()
        if (ipAddress && ctx.event.context!.traits) {
          ctx.event.context!.traits.ip_address = ipAddress
          ctx.event.context!.ip = ipAddress
        }

        const visitedPages = fetchVisitedPages()
        if (visitedPages.length > 0 && ctx.event.context!.traits) {
          ctx.event.context!.traits.visited_pages = visitedPages
        }
      }
    }

    return ctx
  },

  page: async (ctx) => {
    if (analytics) {
      const user = analytics.user()
      if (user) {
        const traits = analytics.user().traits()
        ctx.event.context!.traits = traits

        const ipAddress = await fetchUserIpAddress()
        if (ipAddress && ctx.event.context!.traits) {
          ctx.event.context!.traits.ip_address = ipAddress
          ctx.event.context!.ip = ipAddress
        }

        const visitedPages = fetchVisitedPages()
        if (visitedPages.length > 0 && ctx.event.context!.traits) {
          ctx.event.context!.traits.visited_pages = visitedPages
        }
      }
    }
    return ctx
  },
}

export const addMissedPageEvent = (path: string, url: string) => {
  missed_events.push({ event: { type: "page", path, url } })
}

export const trackSegmentPageEvent = (path: string, url: string): void => {
  if (!isSegmentEnabled) return

  if (typeof window === "undefined" || !window.analytics) {
    addMissedPageEvent(path, url)
    return console.warn(`Failed to track page event: segment is undefined`)
  }

  return window.analytics.page({ path, url })
}

export const addMissedTrackEvent = (
  event: string,
  data: Record<string, any>
) => {
  missed_events.push({ event: { type: "track", event, data } })
}

export const trackSegmentEvent = (
  event: string,
  data: Record<string, any> = {}
): void => {
  if (!isSegmentEnabled) return

  if (typeof window === "undefined" || !window.analytics) {
    addMissedTrackEvent(event, data)
    return console.warn(`Failed to track event: segment is undefined`)
  }

  return window.analytics.track(event, data)
}

export const identifySegmentUser = (
  userId?: string,
  traits: Record<string, any> = {}
): void => {
  if (!isSegmentEnabled) return

  if (typeof window === "undefined" || !window.analytics) {
    return console.warn(`Failed to identify user: segment is undefined`)
  }

  return window.analytics.identify(userId, traits)
}

export const getCookie = (name: string) => {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1]
}

export const isMobileDevice = () => {
  const conditions = [
    "Tablet",
    "Mobile",
    "Mobile Safari",
    "IEMobile/xyz",
    "Mobile/xyz",
    "Edge/ ",
  ]

  return conditions.some((condition) => navigator.userAgent.includes(condition))
}

export const trackGtmAddToCart = (
  addedProduct: YocoCom.ConsolidatedCartItem
): void => {
  if (typeof window === "undefined" || !window.dataLayer) return

  const price = Number(addedProduct.price)

  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event: "add_to_cart",
    ecommerce: {
      currency: "ZAR",
      value: price * addedProduct.quantity,
      items: [
        {
          item_id: isConsolidatedProduct(addedProduct)
            ? addedProduct.sku
            : addedProduct.bundleCode,
          item_name: addedProduct.name,
          item_category: addedProduct.type,
          price,
          quantity: addedProduct.quantity,
        },
      ],
    },
  })
}

interface GtmViewItem {
  item_id: string
  item_name?: string
  item_category?: string
  price: number
}

export const trackGtmViewItem = (item: GtmViewItem): void => {
  if (typeof window === "undefined" || !window.dataLayer) return

  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event: "view_item",
    ecommerce: {
      currency: "ZAR",
      value: item.price,
      items: [item],
    },
  })
}

const getPurchasableItemId = (
  item: YocoCom.ConsolidatedPurchasableItem
): string => (item.type === "product" ? item.sku : item.bundleCode)

export const trackGtmViewItemList = (
  products: YocoCom.ConsolidatedPurchasableItem[],
  itemListId: string,
  itemListName: string
): void => {
  if (typeof window === "undefined" || !window.dataLayer) return

  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event: "view_item_list",
    ecommerce: {
      currency: "ZAR",
      item_list_id: itemListId,
      item_list_name: itemListName,
      items: products.map((product) => ({
        item_id: getPurchasableItemId(product),
        item_name: product.name,
        item_category: product.type,
        price: Number(product.price),
        item_list_id: itemListId,
        item_list_name: itemListName,
      })),
    },
  })
}

export const trackProductViewed = (
  product: YocoCom.ConsolidatedPurchasableItem | null | undefined,
  fallbackProductId?: string
): void => {
  const url = globalThis.location.pathname

  if (product) {
    const id = getPurchasableItemId(product)
    const name = product.name
    const price = Number.parseFloat(product.price)

    trackSegmentEvent("Product Viewed", {
      product_id: id,
      sku: id,
      name,
      content_type: "product",
      price,
      value: price,
      currency: "ZAR", //TODO: we should update consolidatedItem to store this as we get it from the SDK
      url,
    })

    trackGtmViewItem({
      item_id: id,
      item_name: name,
      item_category: product.type,
      price,
    })
  } else if (fallbackProductId) {
    trackSegmentEvent("Product Viewed", {
      product_id: fallbackProductId,
      price: 0.0,
      url,
      content_type: "product",
    })

    trackGtmViewItem({
      item_id: fallbackProductId,
      price: 0.0,
    })
  }
}

export const trackProductListViewed = (
  products: YocoCom.ConsolidatedPurchasableItem[],
  listId: string,
  category: string
): void => {
  trackSegmentEvent("Product List Viewed", {
    list_id: listId,
    category,
    products: products.map((product) => ({
      product_id: getPurchasableItemId(product),
      name: product.name,
      price: Number(product.price),
      quantity: 1,
      type: product.type,
      currency: "ZAR",
    })),
    url: globalThis.location.pathname,
  })

  trackGtmViewItemList(products, listId, category)
}

export const trackAddToCart = (
  addedProduct: YocoCom.ConsolidatedCartItem,
  wasRecommended: boolean = false
) => {
  trackSegmentEvent("Product Added", {
    name: addedProduct.name,
    product_id: isConsolidatedProduct(addedProduct)
      ? addedProduct.sku
      : addedProduct.bundleCode,
    sku: isConsolidatedProduct(addedProduct)
      ? addedProduct.sku
      : addedProduct.bundleCode,
    category: isConsolidatedProduct(addedProduct)
      ? addedProduct.type
      : "bundle",
    price: Number(addedProduct.price),
    value: Number(addedProduct.price),
    quantity: addedProduct.quantity,
    was_recommended: wasRecommended,
    currency: "ZAR",
  })
  trackGtmAddToCart(addedProduct)
}

export const trackShopFilterSelected = (label: string) => {
  trackSegmentEvent("Shop Filter Selected", { label })
}

export const storeCampaignInSessionStorage = (campaign: {
  source?: string
  medium?: string
  content?: string
  name?: string
}) => {
  if (sessionStorageAvailable()) {
    let updated = false
    if (campaign.source && !sessionStorage.getItem("utm_source_session")) {
      sessionStorage.setItem("utm_source_session", campaign.source)
      updated = true
    }
    if (campaign.medium && !sessionStorage.getItem("utm_medium_session")) {
      sessionStorage.setItem("utm_medium_session", campaign.medium)
      updated = true
    }
    if (campaign.content && !sessionStorage.getItem("utm_content_session")) {
      sessionStorage.setItem("utm_content_session", campaign.content)
      updated = true
    }
    if (campaign.name && !sessionStorage.getItem("utm_campaign_session")) {
      sessionStorage.setItem("utm_campaign_session", campaign.name)
      updated = true
    }

    if (updated) {
      identifyUserWithCampaignData()
    }
  }
}

export const identifyUserWithCampaignData = () => {
  identifySegmentUser(undefined, getStoredCampaign())
}

export const getStoredCampaign = () => {
  if (sessionStorageAvailable()) {
    return {
      utm_source_session: sessionStorage.getItem("utm_source_session"),
      utm_medium_session: sessionStorage.getItem("utm_medium_session"),
      utm_content_session: sessionStorage.getItem("utm_content_session"),
      utm_campaign_session: sessionStorage.getItem("utm_campaign_session"),
    }
  }
}

export const triggerSegmentIntegration = () => {
  if (!isSegmentEnabled) return

  let retries = 0
  const maxRetries = 20
  const checkIfLoaded = () => {
    if (
      window.analytics &&
      window.convert?.data?.experiences &&
      window.convert?.currentData?.experiences
    ) {
      const data = getIntegrationData()

      for (const {
        experienceId,
        variationName,
        variationId,
        experienceName,
      } of data)
        window.analytics.track("Convert Event", {
          "Campaign id": experienceId,
          "Experiment name": experienceName,
          "Variant name": variationName,
          "Variant id": variationId,
        })
    } else {
      retries++
      if (retries > maxRetries) {
        console.warn(
          "Failed to load ConvertSegment integration after 10 retries"
        )
        return
      }
      setTimeout(checkIfLoaded, 500)
    }
  }

  setTimeout(checkIfLoaded, 500)
}

const getIntegrationData = () => {
  const data: any = []

  for (const experienceId in window.convert.currentData.experiences) {
    const variationId =
      window.convert.currentData.experiences[experienceId].variation.id
    const experienceName =
      window.convert.data.experiences.find(
        ({ id }: { id: any }) => String(id) === String(experienceId)
      )?.name || "unknown experience name"
    const variationName =
      window.convert.currentData.experiences[experienceId].variation.name ||
      "unknown variation name"
    data.push({
      experienceId,
      experienceName,
      variationId,
      variationName,
    })
  }
  return data
}
